import type { AiToolkitAssistantTask } from "@mem-client/ai-toolkit/contract";
import type { AssistantTaskObservable } from "@/store/assistant/AssistantTaskObservable";
import {
  AssistantChangeRequestChangeKind,
  AssistantTaskStatus,
  type AssistantTaskChangeRequestModelData,
  type AssistantTaskModelData,
} from "@/store/assistant/types";
import type { SyncUpdateValue } from "@/store/sync/types";

type AssistantTaskSource = Pick<
  AssistantTaskObservable,
  | "id"
  | "assistantId"
  | "projectId"
  | "title"
  | "description"
  | "status"
  | "dueAt"
  | "createdAt"
  | "updatedAt"
>;

export const buildAssistantTask = ({
  task,
}: {
  task: AssistantTaskSource;
}): AiToolkitAssistantTask => ({
  id: task.id,
  assistantId: task.assistantId,
  projectId: task.projectId,
  title: task.title,
  description: task.description,
  status: task.status,
  dueAt: task.dueAt,
  createdAt: task.createdAt,
  updatedAt: task.updatedAt,
});

export const buildAssistantTaskFromSyncUpdateValue = ({
  updateValue,
}: {
  updateValue: SyncUpdateValue<AssistantTaskModelData>;
}): AiToolkitAssistantTask => {
  const task = updateValue.model_data;

  return {
    id: updateValue.model_id,
    assistantId: task.assistant_id,
    projectId: task.project_id,
    title: task.title,
    description: task.description,
    status: task.status,
    dueAt: task.due_at,
    createdAt: task.created_at,
    updatedAt: task.updated_at,
  };
};

export const isCreateTaskChangeRequestUpdateValue = ({
  updateValue,
}: {
  updateValue: SyncUpdateValue<AssistantTaskChangeRequestModelData>;
}): boolean => updateValue.model_data.change_kind === AssistantChangeRequestChangeKind.Create;

export const buildAssistantTaskFromCreateChangeRequestUpdateValue = ({
  updateValue,
}: {
  updateValue: SyncUpdateValue<AssistantTaskChangeRequestModelData>;
}): AiToolkitAssistantTask => {
  const changeRequest = updateValue.model_data;

  return {
    id: updateValue.model_id,
    assistantId: changeRequest.assistant_id,
    projectId: changeRequest.requested_project_id,
    title: changeRequest.requested_title ?? "",
    description: changeRequest.requested_description,
    status: changeRequest.requested_status ?? AssistantTaskStatus.Pending,
    dueAt: changeRequest.requested_due_at,
    createdAt: changeRequest.created_at,
    updatedAt: changeRequest.updated_at,
    pending: true,
  };
};
